'use client';
import { tv, type VariantProps } from 'tailwind-variants';
import { z } from 'zod';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const BreadcrumbSchema = z.object({
  href: z.string().url().optional(),
  label: z.string(),
});

const trackingData = {
  data: {
    // biome-ignore lint/style/useNamingConvention: <external data>
    contentPlacementCD: 'top breadcrumb navigation',
    eventAction: 'click breadcrumb link',
    eventCategory: 'navigation',
  },
};

type Breadcrumb = z.infer<typeof BreadcrumbSchema>;

const breadcrumbs = tv({
  slots: {
    breadcrumbContainer: 'flex-row items-center hidden md:flex gap-2',
    wrapper: 'flex flex-row items-center justify-start gap-2',
  },
});

type BreadcrumbsVariants = VariantProps<typeof breadcrumbs>;

interface BreadcrumbsProps extends BreadcrumbsVariants {
  items: Breadcrumb[];
}

const breadcrumb = tv({
  slots: {
    link: 'text-primary-hover transition-colors duration-200 whitespace-pre',
    wrapper: 'flex flex-row items-center typography-sm font-medium capitalize',
  },
});

type BreadcrumbVariants = VariantProps<typeof breadcrumb>;

interface BreadcrumbProps extends Breadcrumb, BreadcrumbVariants {
  className?: string;
  isEnabled?: boolean;
}

const Breadcrumb = ({ label, href, className, isEnabled = true }: BreadcrumbProps) => {
  const { wrapper, link } = breadcrumb();
  return (
    <nav
      aria-label={`breadcrumb ${label}`}
      className={cn(wrapper(), className)}
    >
      {href && isEnabled ? (
        <Link
          aria-label={`breadcrumb link ${label}`}
          className={link()}
          href={href}
          trackingData={{ data: { ...trackingData.data, eventLabel: label } }}
        >
          {label}
        </Link>
      ) : (
        <span className='text-secondary'>{label}</span>
      )}
    </nav>
  );
};

export const Breadcrumbs = ({ items }: BreadcrumbsProps) => {
  if (items.length === 0) {
    return null;
  }
  const mobileItem = items.length > 1 ? items[items.length - 2] : items[0];
  const { wrapper, breadcrumbContainer } = breadcrumbs();
  return (
    <nav
      aria-label='breadcrumbs'
      className={cn(AnalyticsEvent.ImpressionElement)}
      data-cy={ContentPlacement.Breadcrumbs}
    >
      <span
        className={cn(wrapper(), AnalyticsEvent.ImpressionElement)}
        data-cy={ContentPlacement.MobileBreadcrumbs}
      >
        <Icon
          className='text-secondary md:hidden'
          icon='ChevronLeft'
        />
        <Breadcrumb
          className='md:hidden'
          href={mobileItem.href}
          label={mobileItem.label}
        />
      </span>
      <span
        className={cn(wrapper(), AnalyticsEvent.ImpressionElement)}
        data-cy={ContentPlacement.DesktopBreadcrumbs}
      >
        <span className={cn('icon icon-arrow-right hidden', items.length === 1 && 'md:inline-block')} />
        {items.map(({ label, href }, index) => {
          return (
            <span
              className={breadcrumbContainer()}
              key={label}
            >
              <Breadcrumb
                href={href}
                isEnabled={index !== items.length - 1}
                label={label}
              />
              <Icon
                className='hidden text-secondary data-[islast=true]:hidden md:inline-block'
                data-islast={index === items.length - 1}
                icon='ChevronRight'
              />
            </span>
          );
        })}
      </span>
    </nav>
  );
};
